import React, { useEffect, useMemo, useState } from 'react';
import { classProgrammeApi, organisationApi, termApi } from '../../services/api';
import { useDialogDraft } from '../../hooks/useDialogDraft';
import { buildDraftScopeKey, getDraftForScope, clearDraftForScope } from '../../utils/dialogDraftStore';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';

const currentYear = new Date().getFullYear();

const DEFAULT_PERIODS = [
  {
    name: 'Autumn Term',
    startMonth: 9,
    endMonth: 12,
    endDay: 25,
  },
  {
    name: 'Spring Term',
    startMonth: 1,
    endMonth: 4,
    endDay: 1,
  },
  {
    name: 'Summer Term',
    startMonth: 4,
    endMonth: 7,
    endDay: 31,
  },
];

const MONTH_OPTIONS = [
  ['1', 'Jan'],
  ['2', 'Feb'],
  ['3', 'Mar'],
  ['4', 'Apr'],
  ['5', 'May'],
  ['6', 'Jun'],
  ['7', 'Jul'],
  ['8', 'Aug'],
  ['9', 'Sep'],
  ['10', 'Oct'],
  ['11', 'Nov'],
  ['12', 'Dec'],
];

const WEEK_CHIP_COUNT = 14;

const emptyPeriod = (sequence = 1) => {
  const defaults = DEFAULT_PERIODS[sequence - 1] || {
    name: `Term ${sequence}`,
    startMonth: 9,
    endMonth: 12,
    endDay: 25,
  };

  return {
    name: defaults.name,
    sequence,
    start_rule: { type: 'first_full_week', month: defaults.startMonth },
    end_rule: { type: 'week_before_date', month: defaults.endMonth, day: defaults.endDay },
    default_unavailable_weeks: [],
  };
};

const emptyTemplate = () => ({
  name: 'Default Studio Terms',
  active: true,
  is_default: true,
  terms_per_year: 3,
  default_auto_renew: true,
  periods: [emptyPeriod(1), emptyPeriod(2), emptyPeriod(3)],
});

const emptySlotForm = {
  id: '',
  weekday: 1,
  start_time: '19:30',
  end_time: '22:00',
  label: '',
};

const emptyBookingForm = {
  teacher_id: '',
  teacher_email: '',
  teacher_name: '',
  title: '',
  auto_renew_admin: true,
  auto_renew_user: true,
  auto_accept_admin: false,
  auto_accept_user: false,
  send_invitation: true,
  unavailable_weeks: '',
  unavailable_dates: '',
  billable_start_date: '',
  billable_end_date: '',
  source_booking_id: '',
  status: 'prefilled',
  notes: '',
  admin_reason: '',
  class_programme_id: '',
  public_title_override: '',
  public_description_override: '',
  public_visible: false,
};

const buildBookingForm = (settings = {}) => ({
  ...emptyBookingForm,
  auto_accept_admin: Boolean(settings.default_auto_accept),
  auto_accept_user: Boolean(settings.default_auto_accept),
});

export const programmeFieldsFromBooking = (booking = {}) => ({
  class_programme_id: booking.class_programme_id || '',
  public_title_override: booking.public_title_override || '',
  public_description_override: booking.public_description_override || '',
  public_visible: Boolean(booking.public_visible),
});

export const programmePayloadFromBookingForm = (form = {}) => ({
  class_programme_id: form.class_programme_id || null,
  public_title_override: String(form.public_title_override || '').trim() || null,
  public_description_override: String(form.public_description_override || '').trim() || null,
  public_visible: boolValue(form.public_visible),
});

const getPayload = (response, fallback) => response?.data?.data ?? response?.data ?? fallback;
const asArray = (value) => (Array.isArray(value) ? value : value?.items || value?.templates || value?.terms || value?.slots || []);
const boolValue = (value) => value === true || value === 'true' || value === 1 || value === '1';
const isEditableTerm = (term) => (
  term
  && !boolValue(term.locked)
  && !term.published_at
  && !/published/i.test(String(term.status || ''))
);
const pickPlannerTermId = (terms, currentTermId = '') => {
  const currentTerm = terms.find((term) => String(term.id) === String(currentTermId));
  if (isEditableTerm(currentTerm)) {
    return currentTermId;
  }

  const editableTerms = terms.filter(isEditableTerm);
  const preferredTerm = (editableTerms.length ? editableTerms : terms)
    .slice()
    .sort((left, right) => Number(right.id || 0) - Number(left.id || 0))[0];

  return preferredTerm?.id || '';
};
const compactText = (...values) => (
  values.find((value) => typeof value === 'string' && value.trim())?.trim() || ''
);
const userIdentifier = (user) => (
  user?.id
  || user?.user_id
  || user?.userId
  || user?.uuid
  || user?.user_uuid
  || user?.userUuid
  || ''
);
const userDisplayName = (user) => {
  const joinedName = compactText(user?.first_name || user?.firstName, user?.last_name || user?.lastName)
    ? [user?.first_name || user?.firstName, user?.last_name || user?.lastName].filter(Boolean).join(' ').trim()
    : '';
  return (
    compactText(
      user?.display_name,
      user?.displayName,
      user?.full_name,
      user?.fullName,
      user?.name,
      user?.profile?.display_name,
      user?.profile?.displayName,
      joinedName,
    )
    || `Teacher #${userIdentifier(user) || 'unknown'}`
  );
};
const getBookingTeacher = (booking) => (
  booking?.teacher
  || booking?.teacher_user
  || booking?.teacherUser
  || booking?.user
  || booking?.user_account
  || booking?.userAccount
  || {}
);
const getBookingTeacherId = (booking) => (
  booking?.teacher_user_id
  ?? booking?.teacherUserId
  ?? booking?.teacher_id
  ?? booking?.teacherId
  ?? booking?.user_id
  ?? booking?.userId
  ?? getBookingTeacher(booking)?.id
  ?? getBookingTeacher(booking)?.user_id
  ?? getBookingTeacher(booking)?.uuid
  ?? getBookingTeacher(booking)?.user_uuid
  ?? ''
);
const getBookingTeacherEmail = (booking) => (
  booking?.teacher_email
  || booking?.teacherEmail
  || booking?.user_email
  || booking?.userEmail
  || getBookingTeacher(booking)?.primary_email
  || getBookingTeacher(booking)?.email
  || ''
);
const getBookingTeacherName = (booking) => (
  booking?.teacher_name
  || booking?.teacherName
  || booking?.display_name
  || booking?.displayName
  || getBookingTeacher(booking)?.display_name
  || getBookingTeacher(booking)?.displayName
  || ''
);
const getBookingTeacherLabel = (booking) => (
  getBookingTeacherName(booking) || (getBookingTeacherId(booking) ? `Teacher #${getBookingTeacherId(booking)}` : '')
);
const roleText = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return compactText(value.code, value.name, value.role, value.slug).toLowerCase();
  }
  return String(value).trim().toLowerCase();
};
const isTeacherUser = (user) => [
  user?.org_role,
  user?.orgRole,
  user?.organisation_role,
  user?.organisationRole,
  user?.membership_role,
  user?.membershipRole,
  user?.role,
  user?.system_role,
  user?.systemRole,
  user?.user_role,
  user?.userRole,
  user?.membership?.role,
  user?.organisation_user?.role,
  user?.organisationUser?.role,
].some((role) => roleText(role) === 'teacher');
const WEEKDAYS = [
  ['0', 'Sunday'],
  ['1', 'Monday'],
  ['2', 'Tuesday'],
  ['3', 'Wednesday'],
  ['4', 'Thursday'],
  ['5', 'Friday'],
  ['6', 'Saturday'],
];

const PLANNER_DAYS = [
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
  { value: 0, short: 'Sun', label: 'Sunday' },
];

const PLANNER_START_MINUTES = 18 * 60;
const PLANNER_END_MINUTES = 23 * 60;
const PLANNER_STEP_MINUTES = 30;
const PLANNER_TIME_SLOTS = Array.from(
  { length: (PLANNER_END_MINUTES - PLANNER_START_MINUTES) / PLANNER_STEP_MINUTES },
  (_, index) => PLANNER_START_MINUTES + (index * PLANNER_STEP_MINUTES),
);

const TERM_BOOKING_STATUS_LABELS = {
  prefilled: 'Pre-filled',
  invited: 'Invited',
  accepted: 'Accepted',
  rejected: 'Rejected',
  admin_confirmed: 'Admin confirmed',
  cancelled: 'Cancelled',
};

const formatListInput = (value) => (
  Array.isArray(value) ? value.join(', ') : String(value || '')
);

const parseStringList = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const parseWeeks = (value) => String(value || '')
  .split(',')
  .map((item) => parseInt(item.trim(), 10))
  .filter((item) => Number.isInteger(item) && item > 0);

const dateOnly = (date) => date.toISOString().slice(0, 10);

const parseTimeToMinutes = (value, fallback = PLANNER_START_MINUTES) => {
  const [hours, minutes] = String(value || '').slice(0, 5).split(':').map((part) => Number(part));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  return (hours * 60) + minutes;
};

const formatPlannerTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const clampPlannerMinutes = (minutes) => Math.min(
  PLANNER_END_MINUTES,
  Math.max(PLANNER_START_MINUTES, minutes),
);

const addDays = (dateString, days) => {
  if (!dateString) return '';
  const date = new Date(`${String(dateString).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() + days);
  return dateOnly(date);
};

const weekToUnavailableDate = (termStartDate, weekNumber) => addDays(termStartDate, (Number(weekNumber) - 1) * 7);

const unavailableDateToWeek = (termStartDate, dateString) => {
  if (!termStartDate || !dateString) return null;
  const start = new Date(`${String(termStartDate).slice(0, 10)}T00:00:00Z`);
  const date = new Date(`${String(dateString).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(date.getTime())) return null;
  const days = Math.round((date.getTime() - start.getTime()) / 86400000);
  if (days < 0 || days % 7 !== 0) return null;
  return (days / 7) + 1;
};

const getTermUnavailableWeeks = (term) => asArray(term?.unavailable_dates)
  .filter((entry) => typeof entry === 'object' && entry.unavailable_type === 'week')
  .map((entry) => unavailableDateToWeek(term?.start_date, entry.date))
  .filter((week) => Number.isInteger(week) && week > 0);

const getTermUnavailableDayDates = (term) => asArray(term?.unavailable_dates)
  .filter((entry) => typeof entry === 'string' || entry.unavailable_type !== 'week')
  .map((entry) => (typeof entry === 'string' ? entry : entry.date))
  .filter(Boolean);

const toggleNumberInList = (list, value) => (
  list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value].sort((a, b) => a - b)
);

const WeekChipSelector = ({ label, value = [], onChange, testId }) => {
  const selectedWeeks = Array.isArray(value) ? value : parseWeeks(value);
  return (
    <fieldset className="term-week-chip-field" data-testid={testId}>
      <legend>{label}</legend>
      <div className="term-week-chip-grid">
        {Array.from({ length: WEEK_CHIP_COUNT }, (_, index) => index + 1).map((week) => {
          const selected = selectedWeeks.includes(week);
          return (
            <button
              key={week}
              type="button"
              className={`term-week-chip${selected ? ' selected' : ''}`}
              aria-pressed={selected}
              onClick={() => onChange(toggleNumberInList(selectedWeeks, week))}
            >
              W{week}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
};

const getStatusClass = (booking) => {
  const status = booking?.status || booking?.invitation_status || 'prefilled';
  if (status === 'accepted' || status === 'admin_confirmed') return 'accepted';
  if (status === 'rejected' || status === 'cancelled') return 'rejected';
  if (status === 'invited') return 'invited';
  if (booking?.auto_renew_admin && booking?.auto_renew_user && booking?.auto_accept_admin && booking?.auto_accept_user) return 'auto-accepted';
  if (booking?.auto_renew_admin && booking?.auto_renew_user) return 'needs-confirmation';
  return 'prefilled';
};

const uniqueBookings = (bookings) => {
  const seen = new Set();
  return bookings.filter((booking, index) => {
    if (!booking) return false;
    const key = booking.id
      ?? booking.booking_id
      ?? booking.term_slot_booking_id
      ?? booking.uuid
      ?? `${booking.teacher_id || booking.teacher_user_id || booking.user_id || 'teacher'}-${index}`;
    const normalizedKey = String(key);
    if (seen.has(normalizedKey)) return false;
    seen.add(normalizedKey);
    return true;
  });
};

const getSlotBookings = (slot) => uniqueBookings([
  ...asArray(slot?.bookings),
  ...asArray(slot?.slot_bookings),
  ...asArray(slot?.slotBookings),
  ...asArray(slot?.term_slot_bookings),
  ...asArray(slot?.termSlotBookings),
]);

const getBookingSlotId = (booking) => (
  booking?.slot_id
  ?? booking?.slotId
  ?? booking?.term_slot_id
  ?? booking?.termSlotId
  ?? booking?.term_slot?.id
  ?? booking?.slot?.id
);

const getPlannerSlots = (planner) => {
  const slots = asArray(planner?.slots || planner?.planner || planner);
  const plannerBookings = uniqueBookings([
    ...asArray(planner?.bookings),
    ...asArray(planner?.slot_bookings),
    ...asArray(planner?.slotBookings),
    ...asArray(planner?.term_slot_bookings),
    ...asArray(planner?.termSlotBookings),
  ]);

  if (!plannerBookings.length) {
    return slots.map((slot) => ({ ...slot, bookings: getSlotBookings(slot) }));
  }

  const bookingsBySlot = plannerBookings.reduce((groups, booking) => {
    const slotId = getBookingSlotId(booking);
    if (!slotId) return groups;
    const key = String(slotId);
    groups.set(key, [...(groups.get(key) || []), booking]);
    return groups;
  }, new Map());

  return slots.map((slot) => {
    const slotBookings = uniqueBookings([
      ...getSlotBookings(slot),
      ...(bookingsBySlot.get(String(slot.id)) || []),
    ]);
    return { ...slot, bookings: slotBookings };
  });
};

const getPlannerDay = (slot) => Number(slot?.weekday ?? 1);

const getPlannerDayLabel = (weekday) => (
  PLANNER_DAYS.find((day) => day.value === Number(weekday))?.label || `Weekday ${weekday}`
);

const getSlotTopPercent = (slot) => {
  const start = clampPlannerMinutes(parseTimeToMinutes(slot?.start_time));
  return ((start - PLANNER_START_MINUTES) / (PLANNER_END_MINUTES - PLANNER_START_MINUTES)) * 100;
};

const getSlotHeightPercent = (slot) => {
  const start = clampPlannerMinutes(parseTimeToMinutes(slot?.start_time));
  const end = clampPlannerMinutes(parseTimeToMinutes(slot?.end_time, start + PLANNER_STEP_MINUTES));
  const duration = Math.max(PLANNER_STEP_MINUTES, end - start);
  return (duration / (PLANNER_END_MINUTES - PLANNER_START_MINUTES)) * 100;
};

const slotLabel = (slot) => slot.label || slot.title || 'Weekly slot';
const getPrimarySlotBooking = (slot) => getSlotBookings(slot)[0] || null;

const PlannerSlotBlock = ({ slot, onOpenBookingEditor }) => {
  const booking = getPrimarySlotBooking(slot);
  const label = slotLabel(slot);
  const teacherLabel = getBookingTeacherLabel(booking);

  return (
    <button
      type="button"
      className="term-week-slot-block"
      style={{
        top: `${getSlotTopPercent(slot)}%`,
        height: `${getSlotHeightPercent(slot)}%`,
      }}
      onClick={() => onOpenBookingEditor(slot)}
      title={`${label} ${String(slot.start_time || '').slice(0, 5)}-${String(slot.end_time || '').slice(0, 5)}`}
    >
      <strong>{label}</strong>
      <span>{String(slot.start_time || '').slice(0, 5)}-{String(slot.end_time || '').slice(0, 5)}</span>
      {teacherLabel && <span>{teacherLabel}</span>}
    </button>
  );
};

const TermPlannerMockups = ({
  plannerSlots,
  slotForm,
  setSlotForm,
  openBookingEditor,
  selectedTermId,
  createSlotFromSelection,
}) => {
  const [view, setView] = useState('draw');
  const [draftSelection, setDraftSelection] = useState(null);

  const selectTimeRange = (weekday, startMinutes, endMinutes, label = '') => {
    const start = clampPlannerMinutes(Math.min(startMinutes, endMinutes));
    const end = clampPlannerMinutes(Math.max(startMinutes, endMinutes) + PLANNER_STEP_MINUTES);
    setSlotForm({
      ...slotForm,
      id: '',
      weekday,
      start_time: formatPlannerTime(start),
      end_time: formatPlannerTime(end),
      label: label || slotForm.label || '',
    });
  };

  const selectDrawRange = (weekday, startMinutes, endMinutes, label = '') => {
    const start = clampPlannerMinutes(Math.min(startMinutes, endMinutes));
    const rawEnd = Math.max(startMinutes, endMinutes);
    const end = clampPlannerMinutes(rawEnd + PLANNER_STEP_MINUTES);
    setSlotForm({
      ...slotForm,
      id: '',
      weekday,
      start_time: formatPlannerTime(start),
      end_time: formatPlannerTime(end),
      label: label || slotForm.label || '',
    });
  };

  const startDraw = (weekday, minutes) => {
    setDraftSelection({ weekday, start: minutes, end: minutes });
    selectTimeRange(weekday, minutes, minutes);
  };

  const extendDraw = (weekday, minutes) => {
    if (!draftSelection || draftSelection.weekday !== weekday) return;
    const nextSelection = { ...draftSelection, end: minutes };
    setDraftSelection(nextSelection);
    selectDrawRange(weekday, nextSelection.start, nextSelection.end);
  };

  const finishDraw = async (finalWeekday, finalMinutes) => {
    if (draftSelection) {
      const selectionEnd = finalWeekday === draftSelection.weekday && Number.isFinite(finalMinutes)
        ? finalMinutes
        : draftSelection.end;
      const start = clampPlannerMinutes(Math.min(draftSelection.start, selectionEnd));
      const rawEnd = Math.max(draftSelection.start, selectionEnd);
      const end = clampPlannerMinutes(rawEnd + PLANNER_STEP_MINUTES);
      await createSlotFromSelection({
        ...slotForm,
        id: '',
        weekday: draftSelection.weekday,
        start_time: formatPlannerTime(start),
        end_time: formatPlannerTime(end),
        label: slotForm.label || 'New booking',
      });
    }
    setDraftSelection(null);
  };

  const slotsByDay = PLANNER_DAYS.map((day) => ({
    ...day,
    slots: plannerSlots.filter((slot) => getPlannerDay(slot) === day.value),
  }));

  return (
    <section className="term-week-planner" data-testid="WEB-TERM-025">
      <div className="term-section-heading">
        <h3>Term Planner</h3>
        <div className="term-planner-view-tabs" role="tablist" aria-label="Term planner mockup views">
          {[
            ['draw', 'Draw'],
            ['board', 'Board'],
            ['dense', 'Dense'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              className={`term-planner-view-tab${view === id ? ' active' : ''}`}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'draw' && (
        <div className="term-week-draw-shell" onMouseLeave={() => finishDraw()}>
          <div className="term-week-time-rail">
            <span />
            {PLANNER_TIME_SLOTS.map((minutes) => (
              <span key={minutes}>{formatPlannerTime(minutes)}</span>
            ))}
          </div>
          {slotsByDay.map((day) => (
            <div key={day.value} className="term-week-day-column">
              <div className="term-week-day-heading">
                <strong>{day.short}</strong>
                <span>{day.label}</span>
              </div>
              <div className="term-week-day-cells">
                {PLANNER_TIME_SLOTS.map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    className="term-week-time-cell"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      startDraw(day.value, minutes);
                    }}
                    onPointerEnter={() => extendDraw(day.value, minutes)}
                    onPointerUp={() => finishDraw(day.value, minutes)}
                    onPointerCancel={() => finishDraw()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        createSlotFromSelection({
                          ...slotForm,
                          id: '',
                          weekday: day.value,
                          start_time: formatPlannerTime(minutes),
                          end_time: formatPlannerTime(minutes + PLANNER_STEP_MINUTES),
                          label: slotForm.label || 'New booking',
                        });
                      }
                    }}
                    disabled={!selectedTermId}
                    aria-label={`${day.label} ${formatPlannerTime(minutes)}`}
                  />
                ))}
                {draftSelection?.weekday === day.value && (
                  <div
                    className="term-week-draft-block"
                    style={{
                      top: `${((clampPlannerMinutes(Math.min(draftSelection.start, draftSelection.end)) - PLANNER_START_MINUTES) / (PLANNER_END_MINUTES - PLANNER_START_MINUTES)) * 100}%`,
                      height: `${((Math.abs(draftSelection.end - draftSelection.start) + PLANNER_STEP_MINUTES) / (PLANNER_END_MINUTES - PLANNER_START_MINUTES)) * 100}%`,
                    }}
                    aria-hidden="true"
                  >
                    <strong>{slotForm.label || 'New booking'}</strong>
                    <span>
                      {formatPlannerTime(clampPlannerMinutes(Math.min(draftSelection.start, draftSelection.end)))}-
                      {formatPlannerTime(clampPlannerMinutes(
                        draftSelection.start === draftSelection.end
                          ? draftSelection.start + PLANNER_STEP_MINUTES
                          : Math.max(draftSelection.start, draftSelection.end) + PLANNER_STEP_MINUTES,
                      ))}
                    </span>
                  </div>
                )}
                {day.slots.map((slot) => (
                  <PlannerSlotBlock key={slot.id} slot={slot} onOpenBookingEditor={openBookingEditor} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'board' && (
        <div className="term-week-board">
          {slotsByDay.map((day) => (
            <article key={day.value} className="term-week-board-day">
              <h4>{day.label}</h4>
              <div className="term-week-board-actions">
                {[18, 19, 20, 21].map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => selectTimeRange(day.value, hour * 60, (hour * 60) + 30)}
                    disabled={!selectedTermId}
                  >
                    {formatPlannerTime(hour * 60)}
                  </button>
                ))}
              </div>
              <div className="term-week-board-slots">
                {day.slots.length === 0 ? (
                  <span className="term-week-empty-slot">No slots</span>
                ) : day.slots.map((slot) => {
                  const booking = getPrimarySlotBooking(slot);
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      className="term-week-board-slot"
                      onClick={() => openBookingEditor(slot)}
                    >
                      <strong>{slotLabel(slot)}</strong>
                      <span>{String(slot.start_time || '').slice(0, 5)}-{String(slot.end_time || '').slice(0, 5)}</span>
                      {booking && (
                        <span>{getBookingTeacherLabel(booking) || 'No teacher assigned'}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'dense' && (
        <div className="term-week-dense-wrap">
          <div className="term-week-dense-grid">
            <div className="term-week-dense-head">Time</div>
            {PLANNER_DAYS.map((day) => (
              <div key={day.value} className="term-week-dense-head">{day.short}</div>
            ))}
            {PLANNER_TIME_SLOTS.map((minutes) => (
              <React.Fragment key={minutes}>
                <div className="term-week-dense-time">{formatPlannerTime(minutes)}</div>
                {PLANNER_DAYS.map((day) => {
                  const matchingSlot = plannerSlots.find((slot) => {
                    const start = parseTimeToMinutes(slot.start_time);
                    const end = parseTimeToMinutes(slot.end_time, start + PLANNER_STEP_MINUTES);
                    return getPlannerDay(slot) === day.value && minutes >= start && minutes < end;
                  });
                  return (
                    <button
                      key={`${day.value}-${minutes}`}
                      type="button"
                      className={`term-week-dense-cell${matchingSlot ? ' filled' : ''}`}
                      onClick={() => {
                        if (!matchingSlot) {
                          selectTimeRange(day.value, minutes, minutes);
                          return;
                        }
                        openBookingEditor(matchingSlot);
                      }}
                      disabled={!selectedTermId && !matchingSlot}
                      title={matchingSlot ? slotLabel(matchingSlot) : `${day.label} ${formatPlannerTime(minutes)}`}
                    >
                      {matchingSlot ? slotLabel(matchingSlot) : ''}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const normalizePeriod = (period, index) => ({
  ...emptyPeriod(index + 1),
  ...period,
  sequence: index + 1,
  start_rule: {
    ...emptyPeriod(index + 1).start_rule,
    ...(period?.start_rule || {}),
    type: period?.start_rule?.type === 'date' ? 'exact_date' : (period?.start_rule?.type || emptyPeriod(index + 1).start_rule.type),
  },
  end_rule: {
    ...emptyPeriod(index + 1).end_rule,
    ...(period?.end_rule || {}),
    type: period?.end_rule?.type === 'date' ? 'exact_date' : (period?.end_rule?.type || emptyPeriod(index + 1).end_rule.type),
  },
  default_unavailable_weeks: Array.isArray(period?.default_unavailable_weeks)
    ? period.default_unavailable_weeks
    : parseWeeks(period?.default_unavailable_weeks),
});

const normalizeTemplate = (template) => ({
  ...emptyTemplate(),
  ...template,
  active: template?.active !== false,
  is_default: Boolean(template?.is_default),
  default_auto_renew: template?.default_auto_renew !== false,
  periods: asArray(template?.periods).length
    ? asArray(template.periods).map(normalizePeriod)
    : emptyTemplate().periods,
});

const normalizeSettings = (settings) => ({
  default_auto_accept: Boolean(settings?.default_auto_accept),
});

const templatePeriodPayload = (period, index) => {
  const normalized = normalizePeriod(period, index);
  return {
    name: normalized.name,
    sequence: index + 1,
    start_rule: normalized.start_rule,
    end_rule: normalized.end_rule,
    default_unavailable_weeks: normalized.default_unavailable_weeks,
  };
};

const templatePayload = (template) => ({
  name: template.name,
  active: Boolean(template.active),
  is_default: Boolean(template.is_default),
  terms_per_year: template.periods.length,
  default_auto_renew: Boolean(template.default_auto_renew),
  periods: template.periods.map(templatePeriodPayload),
});

const slotPayload = (slot) => ({
  weekday: Number(slot.weekday),
  start_time: slot.start_time,
  end_time: slot.end_time,
  label: slot.label || null,
});

const termInstancePayload = (term) => ({
  name: term.name,
  start_date: (term.start_date || '').slice(0, 10),
  end_date: (term.end_date || '').slice(0, 10),
  unavailable_dates: [
    ...getTermUnavailableDayDates(term).map((date) => ({ date, unavailable_type: 'day' })),
    ...(term.unavailable_weeks !== undefined ? asArray(term.unavailable_weeks) : getTermUnavailableWeeks(term)).map((week) => ({
      date: weekToUnavailableDate(term.start_date, week),
      unavailable_type: 'week',
    })).filter((entry) => entry.date),
  ],
});

const withSavedBooking = (slots, slotId, savedBooking) => slots.map((slot) => {
  if (String(slot.id) !== String(slotId)) return slot;
  const bookings = getSlotBookings(slot);
  const existingIndex = bookings.findIndex((booking) => String(booking.id) === String(savedBooking.id));
  const nextBookings = existingIndex >= 0
    ? bookings.map((booking, index) => (index === existingIndex ? savedBooking : booking))
    : [...bookings, savedBooking];

  return { ...slot, bookings: nextBookings };
});

const TermManagementPanel = ({ organisations = [], rooms = [], selectedOrgFilter = '', mode = 'all' }) => {
  const [organisationId, setOrganisationId] = useState(selectedOrgFilter || organisations[0]?.id || '');
  const [settings, setSettings] = useState({ default_auto_accept: false });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateForm, setTemplateForm] = useState(emptyTemplate());
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [roomTerms, setRoomTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [termDetail, setTermDetail] = useState(null);
  const [plannerSlots, setPlannerSlots] = useState([]);
  const [slotForm, setSlotForm] = useState(emptySlotForm);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [bookingForm, setBookingForm] = useState(emptyBookingForm);
  const [editingBooking, setEditingBooking] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [slotNameDraft, setSlotNameDraft] = useState('');
  const [generateYear, setGenerateYear] = useState(currentYear);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const showSettings = mode === 'all' || mode === 'settings';
  const showBookings = mode === 'all' || mode === 'bookings';
  // M004-F0613 / T-M004-0020: booking editor drawer. New bookings are scoped to the
  // weekly slot (no booking record exists yet); edits are scoped to the booking's id.
  const drawerOpen = showBookings && Boolean(selectedSlotId || editingBooking);
  const newTermBookingDraft = useDialogDraft('term-booking-new', selectedSlotId, { active: drawerOpen && !editingBooking });
  const editTermBookingDraft = useDialogDraft('term-booking-edit', editingBooking?.id, { active: drawerOpen && Boolean(editingBooking) });
  const termBookingDraftRef = editingBooking ? editTermBookingDraft.containerRef : newTermBookingDraft.containerRef;

  const scopedRooms = useMemo(() => {
    if (!organisationId) return rooms;
    return rooms.filter((room) => String(room.organisation_id) === String(organisationId));
  }, [organisationId, rooms]);

  useEffect(() => {
    if (selectedOrgFilter) {
      setOrganisationId(selectedOrgFilter);
    } else if (!organisationId && organisations[0]?.id) {
      setOrganisationId(organisations[0].id);
    }
  }, [selectedOrgFilter, organisations, organisationId]);

  useEffect(() => {
    const selectedRoomInScope = scopedRooms.some((room) => String(room.id) === String(selectedRoomId));
    if (!selectedRoomInScope) {
      const nextRoomId = scopedRooms[0]?.id || '';
      setSelectedRoomId(nextRoomId);
      setRoomTerms([]);
      setSelectedTermId('');
      setTermDetail(null);
      setPlannerSlots([]);
    }
  }, [selectedRoomId, scopedRooms]);

  const loadTemplates = async (nextOrganisationId = organisationId) => {
    if (!nextOrganisationId) return;
    setLoading(true);
    setError('');
    try {
      const [settingsResponse, templatesResponse] = await Promise.all([
        termApi.getSettings(nextOrganisationId),
        termApi.listTemplates(nextOrganisationId),
      ]);
      setSettings(normalizeSettings(getPayload(settingsResponse, {})));
      const nextTemplates = asArray(getPayload(templatesResponse, []));
      setTemplates(nextTemplates);
      const nextSelected = nextTemplates.find((template) => template.is_default)?.id || nextTemplates[0]?.id || '';
      setSelectedTemplateId(nextSelected);
      setTemplateForm(nextSelected ? normalizeTemplate(nextTemplates.find((template) => String(template.id) === String(nextSelected))) : emptyTemplate());
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to load term templates.');
    } finally {
      setLoading(false);
    }
  };

  const loadRoomTerms = async (roomId = selectedRoomId) => {
    if (!roomId) return;
    setError('');
    try {
      const response = await termApi.listRoomTerms(roomId);
      const nextTerms = asArray(getPayload(response, []));
      setRoomTerms(nextTerms);
      setSelectedTermId((currentTermId) => pickPlannerTermId(nextTerms, currentTermId));
    } catch (err) {
      setRoomTerms([]);
      setSelectedTermId('');
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to load room terms.');
    }
  };

  const loadPlanner = async (termId = selectedTermId) => {
    if (!termId) {
      setTermDetail(null);
      setPlannerSlots([]);
      return;
    }
    setError('');
    try {
      const [termResponse, plannerResponse] = await Promise.all([
        termApi.getTermInstance(termId),
        termApi.getPlanner(termId),
      ]);
      setTermDetail(getPayload(termResponse, null));
      const planner = getPayload(plannerResponse, []);
      setPlannerSlots(getPlannerSlots(planner));
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to load term planner.');
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [organisationId]);

  useEffect(() => {
    const loadOrgUsers = async () => {
      if (!organisationId) {
        setOrgUsers([]);
        return;
      }
      try {
        const response = await organisationApi.getUsers(organisationId);
        setOrgUsers(asArray(getPayload(response, [])));
      } catch (err) {
        setOrgUsers([]);
        setError(err.response?.data?.message || err.response?.data?.code || 'Unable to load organisation users.');
      }
    };
    loadOrgUsers();
  }, [organisationId]);

  useEffect(() => {
    if (!organisationId) {
      setProgrammes([]);
      return;
    }
    classProgrammeApi.list({ org: organisationId }).then((response) => {
      const payload = getPayload(response, []);
      setProgrammes(Array.isArray(payload) ? payload : (payload.programmes || payload.items || []));
    }).catch(() => setProgrammes([]));
  }, [organisationId]);

  useEffect(() => {
    loadRoomTerms();
  }, [selectedRoomId]);

  useEffect(() => {
    loadPlanner();
  }, [selectedTermId]);

  const selectTemplate = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setTemplateForm(emptyTemplate());
      return;
    }
    const cached = templates.find((template) => String(template.id) === String(templateId));
    setTemplateForm(normalizeTemplate(cached));
    try {
      const response = await termApi.getTemplate(templateId);
      setTemplateForm(normalizeTemplate(getPayload(response, cached)));
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to load template detail.');
    }
  };

  const updateTemplateField = (name, value) => {
    setTemplateForm((current) => ({ ...current, [name]: value }));
  };

  const updatePeriod = (index, patch) => {
    setTemplateForm((current) => ({
      ...current,
      periods: current.periods.map((period, periodIndex) => (
        periodIndex === index ? normalizePeriod({ ...period, ...patch }, periodIndex) : period
      )),
    }));
  };

  const addPeriod = () => {
    setTemplateForm((current) => ({
      ...current,
      terms_per_year: current.periods.length + 1,
      periods: [...current.periods, emptyPeriod(current.periods.length + 1)],
    }));
  };

  const removePeriod = (index) => {
    setTemplateForm((current) => {
      const periods = current.periods.filter((_, periodIndex) => periodIndex !== index)
        .map((period, periodIndex) => normalizePeriod({ ...period, sequence: periodIndex + 1 }, periodIndex));
      return { ...current, terms_per_year: periods.length, periods };
    });
  };

  const saveSettings = async () => {
    setError('');
    setStatus('');
    try {
      await termApi.updateSettings(organisationId, settings);
      setStatus('Term defaults saved.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to save term defaults.');
    }
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    setError('');
    setStatus('');
    const payload = templatePayload(templateForm);
    try {
      const response = selectedTemplateId
        ? await termApi.updateTemplate(selectedTemplateId, payload)
        : await termApi.createTemplate(organisationId, payload);
      const savedTemplate = getPayload(response, payload);
      setSelectedTemplateId(savedTemplate.id || selectedTemplateId);
      setTemplateForm(normalizeTemplate(savedTemplate));
      await loadTemplates(organisationId);
      setStatus('Term template saved.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to save term template.');
    }
  };

  const archiveTemplate = async () => {
    if (!selectedTemplateId || !window.confirm('Archive this term template?')) return;
    setError('');
    setStatus('');
    try {
      await termApi.deleteTemplate(selectedTemplateId);
      setSelectedTemplateId('');
      setTemplateForm(emptyTemplate());
      await loadTemplates(organisationId);
      setStatus('Term template archived.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to archive term template.');
    }
  };

  const generateDraftTerms = async () => {
    if (!selectedTemplateId) return;
    setError('');
    setStatus('');
    try {
      const response = await termApi.generateTerms(selectedTemplateId, { year: Number(generateYear), terms_ahead: templateForm.periods.length });
      const generated = getPayload(response, {});
      await loadRoomTerms();
      setStatus(`Draft terms generated${generated.created_instances?.length ? `: ${generated.created_instances.length}` : ''}.`);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to generate room terms.');
    }
  };

  const updateTermState = async (patch) => {
    if (!selectedTermId) return;
    setError('');
    setStatus('');
    try {
      const response = await termApi.updateTermInstance(selectedTermId, termInstancePayload(patch));
      setTermDetail(getPayload(response, { ...termDetail, ...patch }));
      setStatus('Term instance saved.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to save term instance.');
    }
  };

  const publishTerm = async () => {
    if (!selectedTermId || !window.confirm('Publish this term and create calendar events?')) return;
    setError('');
    setStatus('');
    try {
      const response = await termApi.publishTermInstance(selectedTermId);
      setTermDetail(getPayload(response, termDetail));
      setStatus('Term published. Calendar sync errors will remain visible and can be refreshed.');
      await loadPlanner(selectedTermId);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to publish term.');
    }
  };

  const prefillFutureTerm = async () => {
    if (!selectedTermId) return;
    setError('');
    setStatus('');
    try {
      const response = await termApi.prefillTermInstance(selectedTermId);
      const result = getPayload(response, {});
      const created = asArray(result.created);
      const skipped = asArray(result.skippedBookingIds || result.skipped_booking_ids);
      await loadPlanner(selectedTermId);
      setStatus(`Future term pre-fill complete: ${created.length} copied${skipped.length ? `, ${skipped.length} skipped` : ''}.`);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to pre-fill future term.');
    }
  };

  const refreshCalendar = async () => {
    if (!selectedTermId) return;
    setError('');
    setStatus('');
    try {
      await termApi.refreshCalendar(selectedTermId);
      setStatus('Calendar refresh requested.');
      await loadPlanner(selectedTermId);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to refresh calendar sync.');
    }
  };

  const createSlot = async (event) => {
    event.preventDefault();
    if (!selectedTermId) return;
    setError('');
    setStatus('');
    const wasEditingSlot = Boolean(slotForm.id);
    try {
      let savedSlot = null;
      if (slotForm.id) {
        await termApi.updateSlot(slotForm.id, slotPayload(slotForm));
      } else {
        const response = await termApi.createSlot(selectedTermId, slotPayload(slotForm));
        savedSlot = getPayload(response, slotForm);
      }
      await loadPlanner(selectedTermId);
      if (!wasEditingSlot && savedSlot?.id) {
        setSelectedSlotId(savedSlot.id);
        setEditingBooking(null);
        setSlotNameDraft(savedSlot.label || slotForm.label || '');
        setSlotForm({
          id: savedSlot.id,
          weekday: Number(savedSlot.weekday ?? slotForm.weekday),
          start_time: String(savedSlot.start_time || slotForm.start_time).slice(0, 5),
          end_time: String(savedSlot.end_time || slotForm.end_time).slice(0, 5),
          label: savedSlot.label || slotForm.label || '',
        });
        setBookingForm(buildBookingForm(settings));
        setStatus('Planner slot added. Add a teacher booking.');
      } else {
        setSlotForm(emptySlotForm);
        setStatus('Planner slot updated.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to add planner slot.');
    }
  };

  const createSlotFromSelection = async (draftSlot) => {
    if (!selectedTermId) return;
    setError('');
    setStatus('');
    try {
      const response = await termApi.createSlot(selectedTermId, slotPayload(draftSlot));
      const savedSlot = getPayload(response, draftSlot);
      setPlannerSlots((current) => [
        ...current,
        {
          ...draftSlot,
          ...savedSlot,
          bookings: getSlotBookings(savedSlot),
        },
      ]);
      setSlotForm({
        id: savedSlot.id || '',
        weekday: Number(savedSlot.weekday ?? draftSlot.weekday),
        start_time: String(savedSlot.start_time || draftSlot.start_time).slice(0, 5),
        end_time: String(savedSlot.end_time || draftSlot.end_time).slice(0, 5),
        label: savedSlot.label || draftSlot.label || '',
      });
      setSelectedSlotId(savedSlot.id || '');
      setEditingBooking(null);
      setSlotNameDraft(savedSlot.label || draftSlot.label || '');
      setBookingForm(buildBookingForm(settings));
      setStatus('Planner slot added. Add a teacher booking.');
      await loadPlanner(selectedTermId);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to add planner slot.');
    }
  };

  const deleteSlot = async (slotId) => {
    if (!window.confirm('Delete this planner slot?')) return;
    setError('');
    setStatus('');
    try {
      await termApi.deleteSlot(slotId);
      await loadPlanner(selectedTermId);
      setStatus('Planner slot deleted.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to delete planner slot.');
    }
  };

  const saveBooking = async (event) => {
    event.preventDefault();
    const targetSlotId = editingBooking?.slot_id || selectedSlotId;
    if (!targetSlotId && !editingBooking?.id) return;
    // Captured before the success path resets editingBooking/selectedSlotId to blank.
    const bookingDraftScopeKey = editingBooking?.id
      ? buildDraftScopeKey('term-booking-edit', editingBooking.id)
      : buildDraftScopeKey('term-booking-new', targetSlotId);
    setError('');
    setStatus('');
    const payload = {
      teacher_user_id: bookingForm.teacher_id ? Number(bookingForm.teacher_id) : undefined,
      status: bookingForm.status || undefined,
      auto_renew_admin: boolValue(bookingForm.auto_renew_admin),
      auto_renew_user: boolValue(bookingForm.auto_renew_user),
      auto_accept_admin: boolValue(bookingForm.auto_accept_admin),
      auto_accept_user: boolValue(bookingForm.auto_accept_user),
      send_invitation: !editingBooking?.id ? boolValue(bookingForm.send_invitation) : undefined,
      excluded_dates: parseStringList(bookingForm.unavailable_dates),
      source_booking_id: bookingForm.source_booking_id ? Number(bookingForm.source_booking_id) : undefined,
      billable_start_date: bookingForm.billable_start_date || undefined,
      billable_end_date: bookingForm.billable_end_date || undefined,
      admin_note: editingBooking?.id ? (bookingForm.admin_reason || bookingForm.notes || undefined) : undefined,
      ...programmePayloadFromBookingForm(bookingForm),
    };
    try {
      let response;
      if (editingBooking?.id) {
        response = await termApi.updateSlotBooking(editingBooking.id, payload);
        setStatus('Teacher booking updated.');
      } else {
        response = await termApi.createSlotBooking(targetSlotId, payload);
        setStatus(bookingForm.send_invitation ? 'Teacher booking added and invitation queued.' : 'Teacher booking added.');
      }
      const result = getPayload(response, {});
      const rawBooking = result.booking || result;
      const selectedTeacher = teacherOptions.find((teacher) => String(userIdentifier(teacher)) === String(bookingForm.teacher_id));
      const savedBooking = {
        ...rawBooking,
        teacher_id: rawBooking.teacher_id || rawBooking.teacher_user_id || bookingForm.teacher_id,
        teacher_email: rawBooking.teacher_email || rawBooking.user_email || bookingForm.teacher_email,
        user_email: rawBooking.user_email || rawBooking.teacher_email || bookingForm.teacher_email,
        teacher_name: rawBooking.teacher_name || rawBooking.display_name || (selectedTeacher ? userDisplayName(selectedTeacher) : ''),
      };
      if (slotNameDraft && targetSlotId) {
        await termApi.updateSlot(targetSlotId, {
          ...slotPayload({
            ...slotForm,
            id: targetSlotId,
            label: slotNameDraft,
          }),
        });
      }
      setEditingBooking(null);
      setSelectedSlotId('');
      setSlotNameDraft('');
      setBookingForm(buildBookingForm(settings));
      clearDraftForScope(bookingDraftScopeKey);
      await loadPlanner(selectedTermId);
      if (savedBooking?.id) {
        setPlannerSlots((current) => withSavedBooking(current, targetSlotId, savedBooking));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to save teacher booking.');
    }
  };

  const editBooking = (slot, booking) => {
    const teacherId = getBookingTeacherId(booking);
    const teacherEmail = getBookingTeacherEmail(booking);
    const teacherName = getBookingTeacherName(booking);
    setSelectedSlotId(slot.id);
    setEditingBooking({ ...booking, slot_id: slot.id });
    setSlotNameDraft(slot.label || slot.title || '');
    setSlotForm({
      id: slot.id,
      weekday: Number(slot.weekday ?? 1),
      start_time: String(slot.start_time || '19:30').slice(0, 5),
      end_time: String(slot.end_time || '22:00').slice(0, 5),
      label: slot.label || slot.title || '',
    });
    const draft = getDraftForScope(buildDraftScopeKey('term-booking-edit', booking.id));
    setBookingForm({
      ...emptyBookingForm,
      teacher_id: teacherId,
      teacher_email: teacherEmail,
      teacher_name: teacherName,
      title: booking.title || '',
      auto_renew_admin: booking.auto_renew_admin !== false,
      auto_renew_user: booking.auto_renew_user !== false,
      auto_accept_admin: Boolean(booking.auto_accept_admin),
      auto_accept_user: Boolean(booking.auto_accept_user),
      send_invitation: false,
      unavailable_weeks: formatListInput(booking.unavailable_weeks),
      unavailable_dates: formatListInput(booking.excluded_dates || booking.unavailable_dates),
      billable_start_date: (booking.billable_start_date || '').slice(0, 10),
      billable_end_date: (booking.billable_end_date || '').slice(0, 10),
      source_booking_id: booking.source_booking_id || '',
      status: booking.status || 'prefilled',
      notes: booking.notes || '',
      admin_reason: '',
      ...programmeFieldsFromBooking(booking),
      ...draft,
    });
  };

  const editSlot = (slot) => {
    setSlotForm({
      id: slot.id,
      weekday: Number(slot.weekday ?? 1),
      start_time: String(slot.start_time || '19:30').slice(0, 5),
      end_time: String(slot.end_time || '22:00').slice(0, 5),
      label: slot.label || slot.title || '',
    });
  };

  const openNewBookingDialog = (slot) => {
    setSelectedSlotId(slot.id);
    setEditingBooking(null);
    setSlotNameDraft(slot.label || slot.title || '');
    setSlotForm({
      id: slot.id,
      weekday: Number(slot.weekday ?? 1),
      start_time: String(slot.start_time || '19:30').slice(0, 5),
      end_time: String(slot.end_time || '22:00').slice(0, 5),
      label: slot.label || slot.title || '',
    });
    const draft = getDraftForScope(buildDraftScopeKey('term-booking-new', slot.id));
    setBookingForm({ ...buildBookingForm(settings), ...draft });
  };

  const openPlannerBookingEditor = (slot) => {
    const booking = getPrimarySlotBooking(slot);
    if (booking) {
      editBooking(slot, booking);
      return;
    }
    openNewBookingDialog(slot);
  };

  const closeBookingDialog = () => {
    setSelectedSlotId('');
    setEditingBooking(null);
    setSlotNameDraft('');
    setBookingForm(buildBookingForm(settings));
  };

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const bookingDialogDismissHandlers = useDismissOnOutsideClick(closeBookingDialog);

  const teacherOptions = useMemo(() => (
    orgUsers.filter(isTeacherUser)
  ), [orgUsers]);

  const selectedTeacher = teacherOptions.find((teacher) => String(userIdentifier(teacher)) === String(bookingForm.teacher_id));
  const selectedTeacherLabel = selectedTeacher
    ? userDisplayName(selectedTeacher)
    : (bookingForm.teacher_name || (bookingForm.teacher_id ? `Teacher #${bookingForm.teacher_id}` : ''));
  const selectedSlot = plannerSlots.find((slot) => String(slot.id) === String(selectedSlotId))
    || (slotForm.id ? slotForm : null);
  const drawerSlotDay = selectedSlot ? getPlannerDayLabel(getPlannerDay(selectedSlot)) : '';
  const drawerSlotTime = selectedSlot
    ? `${String(selectedSlot.start_time || slotForm.start_time || '').slice(0, 5)}-${String(selectedSlot.end_time || slotForm.end_time || '').slice(0, 5)}`
    : '';

  const updateBookingStatus = async (booking, statusValue, message) => {
    const reason = window.prompt('Admin reason for this booking change?') || '';
    if (!reason.trim()) return;
    setError('');
    setStatus('');
    try {
      await termApi.updateSlotBooking(booking.id, {
        status: statusValue,
        admin_note: reason,
      });
      setStatus(message);
      await loadPlanner(selectedTermId);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to update teacher booking.');
    }
  };

  const sendInvitation = async (bookingId) => {
    setError('');
    setStatus('');
    try {
      await termApi.sendBookingInvitation(bookingId);
      setStatus('Invitation sent.');
      await loadPlanner(selectedTermId);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to send invitation.');
    }
  };

  const confirmBooking = async (bookingId) => {
    setError('');
    setStatus('');
    try {
      await termApi.adminConfirmBooking(bookingId);
      setStatus('Booking confirmed by admin.');
      await loadPlanner(selectedTermId);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to confirm booking.');
    }
  };

  return (
    <section className="term-management-panel" aria-label={showBookings ? 'Term planner management' : 'Term settings'}>
      <div className="term-management-header">
        <div>
          <h2>{showBookings ? 'Term Planner' : 'Term Settings'}</h2>
          <p>
            {showBookings
              ? 'Room terms, weekly planners, teacher bookings, invitations, and calendar sync.'
              : 'Organisation defaults, yearly templates, unavailable weeks, and draft term generation.'}
          </p>
        </div>
        <div className="term-management-controls">
          <span className="term-selected-organisation">
            {organisations.find((org) => String(org.id) === String(organisationId))?.name || 'Select an organisation above'}
          </span>
          <button type="button" className="btn btn-secondary" onClick={() => { loadTemplates(); loadRoomTerms(); }}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="form-error" role="alert">{error}</div>}
      {status && <div className="form-success" role="status">{status}</div>}
      {loading && <div className="loading-spinner">Loading term management...</div>}

      {showSettings && (
        <>
          <div className="term-management-grid">
            <section className="term-management-section" data-testid="WEB-TERM-001">
              <div className="term-section-heading">
                <h3>Organisation Defaults</h3>
                <span>M005-F0001 / WEB-TERM-001</span>
              </div>
              <label className="term-checkbox-row">
                <input
                  type="checkbox"
                  checked={settings.default_auto_accept}
                  onChange={(event) => setSettings({ ...settings, default_auto_accept: event.target.checked })}
                />
                New non-copied slot bookings start with admin and teacher auto-accept enabled
              </label>
              <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={!organisationId}>
                Save Defaults
              </button>
            </section>

            <section className="term-management-section" data-testid="WEB-TERM-002">
              <div className="term-section-heading">
                <h3>Generate Draft Terms</h3>
                <span>M005-F0002 / WEB-TERM-002</span>
              </div>
              <div className="term-inline-fields">
                <label>
                  Template
                  <select value={selectedTemplateId} onChange={(event) => selectTemplate(event.target.value)}>
                    <option value="">New template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}{template.is_default ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Year
                  <input type="number" value={generateYear} min={currentYear - 1} max={currentYear + 5} onChange={(event) => setGenerateYear(event.target.value)} />
                </label>
              </div>
              <button type="button" className="btn btn-primary" onClick={generateDraftTerms} disabled={!selectedTemplateId}>
                Generate For All Active Rooms
              </button>
            </section>
          </div>

          <form className="term-management-section term-template-editor" onSubmit={saveTemplate}>
            <div className="term-section-heading">
              <h3>Template Editor</h3>
              <span>M005-F0001</span>
            </div>
            <div className="term-editor-toolbar">
              <button type="button" className="btn btn-secondary" onClick={addPeriod}>Add Term</button>
            </div>
            <div className="term-template-grid">
              <label>
                Name
                <input value={templateForm.name} onChange={(event) => updateTemplateField('name', event.target.value)} required />
              </label>
              <label className="term-checkbox-row compact">
                <input type="checkbox" checked={templateForm.active} onChange={(event) => updateTemplateField('active', event.target.checked)} />
                Active
              </label>
              <label className="term-checkbox-row compact">
                <input type="checkbox" checked={templateForm.is_default} onChange={(event) => updateTemplateField('is_default', event.target.checked)} />
                Default template
              </label>
              <label className="term-checkbox-row compact">
                <input type="checkbox" checked={templateForm.default_auto_renew} onChange={(event) => updateTemplateField('default_auto_renew', event.target.checked)} />
                <span>Auto-renew term planner booking by default</span>
                <span
                  className="term-info-popout"
                  tabIndex={0}
                  aria-label="Auto-renew copies eligible teacher bookings into future draft terms when pre-fill runs."
                  title="Copies eligible teacher bookings into future draft terms when pre-fill runs."
                >
                  i
                </span>
              </label>
            </div>

            <div className="term-period-list">
              {templateForm.periods.map((period, index) => (
                <div key={`${period.sequence}-${index}`} className="term-period-editor">
                  <div className="term-period-heading">
                    <h4>{period.name || `Term ${index + 1}`}</h4>
                    {templateForm.periods.length > 1 && (
                      <button type="button" className="btn btn-danger btn-small" onClick={() => removePeriod(index)}>
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="term-period-grid">
                    <label>
                      Period name
                      <input value={period.name} onChange={(event) => updatePeriod(index, { name: event.target.value })} required />
                    </label>
                    <label>
                      Start rule
                      <select value={period.start_rule.type} onChange={(event) => updatePeriod(index, { start_rule: { ...period.start_rule, type: event.target.value } })}>
                        <option value="first_full_week">First full week</option>
                        <option value="exact_date">Fixed date</option>
                      </select>
                    </label>
                    <label>
                      Start month
                      <select value={String(period.start_rule.month || 1)} onChange={(event) => updatePeriod(index, { start_rule: { ...period.start_rule, month: Number(event.target.value) } })}>
                        {MONTH_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      End rule
                      <select value={period.end_rule.type} onChange={(event) => updatePeriod(index, { end_rule: { ...period.end_rule, type: event.target.value } })}>
                        <option value="week_before_date">Week before date</option>
                        <option value="exact_date">Fixed date</option>
                      </select>
                    </label>
                    <label>
                      End month
                      <select value={String(period.end_rule.month || 1)} onChange={(event) => updatePeriod(index, { end_rule: { ...period.end_rule, month: Number(event.target.value) } })}>
                        {MONTH_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      End day
                      <input type="number" min={1} max={31} value={period.end_rule.day || 1} onChange={(event) => updatePeriod(index, { end_rule: { ...period.end_rule, day: Number(event.target.value) } })} />
                    </label>
                    <WeekChipSelector
                      label="Unavailable weeks"
                      value={period.default_unavailable_weeks}
                      onChange={(weeks) => updatePeriod(index, { default_unavailable_weeks: weeks })}
                      testId="WEB-TERM-023"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="term-action-row">
              <button type="button" className="btn btn-secondary" onClick={addPeriod}>Add Term</button>
              <button type="submit" className="btn btn-primary" disabled={!organisationId}>Save Template</button>
              <button type="button" className="btn btn-danger" onClick={archiveTemplate} disabled={!selectedTemplateId}>Archive Template</button>
            </div>
          </form>
        </>
      )}

      {showBookings && (
        <section className="term-management-section" data-testid="WEB-TERM-003">
        <div className="term-section-heading">
              <h3>Room Term Planner</h3>
          <span>M005-F0003 to M005-F0011</span>
        </div>
        <div className="term-inline-fields">
          <label>
            Room
            <select value={selectedRoomId} onChange={(event) => setSelectedRoomId(event.target.value)}>
              <option value="">Select room</option>
              {scopedRooms.map((room) => (
                <option key={room.id} value={room.id}>{room.name}</option>
              ))}
            </select>
          </label>
          <label>
            Term
            <select value={selectedTermId} onChange={(event) => setSelectedTermId(event.target.value)}>
              <option value="">Select term</option>
              {roomTerms.map((term) => (
                <option key={term.id} value={term.id}>{term.name || term.template_period_name || `Term #${term.id}`}</option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn-secondary" onClick={() => loadPlanner(selectedTermId)} disabled={!selectedTermId}>
            Reload Planner
          </button>
        </div>

        {termDetail && (
          <div className="term-instance-toolbar">
            <div className="term-instance-state" data-testid="WEB-TERM-011">
              <strong>{termDetail.status || 'draft'}</strong>
              {termDetail.published_at && <span>Published {new Date(termDetail.published_at).toLocaleString('en-GB')}</span>}
              {termDetail.calendar_sync?.status && <span>Calendar {termDetail.calendar_sync.status}</span>}
            </div>
            <label>
              Term name
              <input value={termDetail.name || ''} onChange={(event) => setTermDetail({ ...termDetail, name: event.target.value })} />
            </label>
            <label>
              Start date
              <input type="date" value={(termDetail.start_date || '').slice(0, 10)} onChange={(event) => setTermDetail({ ...termDetail, start_date: event.target.value })} />
            </label>
            <label>
              End date
              <input type="date" value={(termDetail.end_date || '').slice(0, 10)} onChange={(event) => setTermDetail({ ...termDetail, end_date: event.target.value })} />
            </label>
            <WeekChipSelector
              label="Unavailable weeks"
              value={termDetail.unavailable_weeks || getTermUnavailableWeeks(termDetail)}
              onChange={(weeks) => setTermDetail({ ...termDetail, unavailable_weeks: weeks })}
              testId="WEB-TERM-010"
            />
            <label data-testid="WEB-TERM-010">
              Unavailable dates
              <input
                value={formatListInput(getTermUnavailableDayDates(termDetail))}
                onChange={(event) => setTermDetail({
                  ...termDetail,
                  unavailable_weeks: termDetail.unavailable_weeks || getTermUnavailableWeeks(termDetail),
                  unavailable_dates: parseStringList(event.target.value),
                })}
                placeholder="2026-10-31, 2026-12-24"
              />
            </label>
            <button type="button" className="btn btn-secondary" onClick={() => updateTermState(termDetail)}>Save Term</button>
            <button type="button" className="btn btn-secondary" data-testid="WEB-TERM-009" onClick={prefillFutureTerm}>Pre-fill Future Term</button>
            <button type="button" className="btn btn-primary" onClick={publishTerm}>Publish</button>
            <button type="button" className="btn btn-secondary" onClick={refreshCalendar}>Refresh Calendar</button>
          </div>
        )}

        <TermPlannerMockups
          plannerSlots={plannerSlots}
          slotForm={slotForm}
          setSlotForm={setSlotForm}
          openBookingEditor={openPlannerBookingEditor}
          selectedTermId={selectedTermId}
          createSlotFromSelection={createSlotFromSelection}
        />

        <form className="term-slot-form" onSubmit={createSlot} data-testid="WEB-TERM-004">
          <label>
            Weekday
            <select value={slotForm.weekday} onChange={(event) => setSlotForm({ ...slotForm, weekday: Number(event.target.value) })}>
              {WEEKDAYS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            Start
            <input type="time" value={slotForm.start_time} onChange={(event) => setSlotForm({ ...slotForm, start_time: event.target.value })} />
          </label>
          <label>
            End
            <input type="time" value={slotForm.end_time} onChange={(event) => setSlotForm({ ...slotForm, end_time: event.target.value })} />
          </label>
          <label>
            Label
            <input value={slotForm.label} onChange={(event) => setSlotForm({ ...slotForm, label: event.target.value })} placeholder="Adult salsa" />
          </label>
          <button type="submit" className="btn btn-primary" disabled={!selectedTermId}>{slotForm.id ? 'Save Slot' : 'Add Slot'}</button>
          {slotForm.id && (
            <button type="button" className="btn btn-secondary" onClick={() => setSlotForm(emptySlotForm)}>
              New Slot
            </button>
          )}
        </form>

        <div className="term-planner-grid">
          {plannerSlots.length === 0 ? (
            <div className="empty-state">No planner slots yet.</div>
          ) : plannerSlots.map((slot) => (
            <article key={slot.id} className="term-slot-card" data-testid="WEB-TERM-005">
              <div className="term-slot-card-header">
                <div>
                  <h4>{slot.label || slot.title || 'Weekly slot'}</h4>
                  <p>{slot.weekday_label || `Weekday ${slot.weekday}`} {slot.start_time} - {slot.end_time}</p>
                </div>
                <div className="term-booking-actions">
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => editSlot(slot)}>Edit Slot</button>
                  <button type="button" className="btn btn-danger btn-small" onClick={() => deleteSlot(slot.id)}>Delete</button>
                </div>
              </div>
              <div className="term-booking-list">
                {getSlotBookings(slot).map((booking) => (
                  <div key={booking.id} className={`term-booking-row term-booking-${getStatusClass(booking)}`} data-testid="WEB-TERM-006">
                    <button
                      type="button"
                      className="term-booking-summary"
                      aria-label={`Edit ${getBookingTeacherLabel(booking) || 'teacher booking'}`}
                      onClick={() => editBooking(slot, booking)}
                    >
                      <strong>{getBookingTeacherLabel(booking) || 'No teacher assigned'}</strong>
                      <span>
                        {TERM_BOOKING_STATUS_LABELS[booking.status] || booking.status || booking.invitation_status || 'Pre-filled'}
                        {' / sync '}
                        {booking.sync_status || 'pending'}
                      </span>
                      {booking.source_booking_id && (
                        <span data-testid="WEB-TERM-009">Renewed from booking #{booking.source_booking_id}</span>
                      )}
                      {(booking.google_recurring_event_id || booking.imported_recurring_booking_id || booking.imported_from_google || booking.imported_recurring_booking || booking.term_management_required) && (
                        <span data-testid="WEB-TERM-014">Imported recurring calendar booking managed here</span>
                      )}
                      {(booking.notification_status || booking.invitation_expires_at || booking.last_notification_at) && (
                        <span data-testid="WEB-TERM-016">
                          Notification {booking.notification_status || 'pending'}
                          {booking.last_notification_at ? ` / sent ${new Date(booking.last_notification_at).toLocaleString('en-GB')}` : ''}
                          {booking.invitation_expires_at ? ` / expires ${new Date(booking.invitation_expires_at).toLocaleDateString('en-GB')}` : ''}
                        </span>
                      )}
                      {booking.sync_status === 'error' && (
                        <span className="term-sync-error">Calendar sync needs refresh</span>
                      )}
                    </button>
                    <div className="term-booking-actions">
                      <button type="button" className="btn btn-secondary btn-small" onClick={() => editBooking(slot, booking)}>Edit</button>
                      <button type="button" className="btn btn-secondary btn-small" onClick={() => sendInvitation(booking.id)}>Invite</button>
                      <button type="button" className="btn btn-success btn-small" onClick={() => confirmBooking(booking.id)}>Confirm</button>
                      <button type="button" className="btn btn-warning btn-small" data-testid="WEB-TERM-012" onClick={() => updateBookingStatus(booking, 'rejected', 'Booking rejected before term start.')}>Reject</button>
                      <button type="button" className="btn btn-danger btn-small" onClick={() => updateBookingStatus(booking, 'cancelled', 'Booking cancelled.')}>Cancel</button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => openNewBookingDialog(slot)}>
                Add Teacher Booking
              </button>
            </article>
          ))}
        </div>
        </section>
      )}

      {showBookings && (selectedSlotId || editingBooking) && (
        <div className="drawer-overlay term-booking-drawer-overlay" {...bookingDialogDismissHandlers}>
          <aside
            className="term-booking-editor-drawer"
            ref={termBookingDraftRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="term-booking-editor-title"
            data-testid="WEB-TERM-007"
            onClick={(event) => event.stopPropagation()}
          >
            <form className="term-booking-editor term-booking-drawer-form" onSubmit={saveBooking}>
              <div className="drawer-header term-booking-drawer-header">
                <div>
                  <h3 id="term-booking-editor-title">{editingBooking ? 'Edit Teacher Booking' : 'Add Teacher Booking'}</h3>
                </div>
                <button type="button" className="modal-close" onClick={closeBookingDialog} aria-label="Close booking drawer">&times;</button>
              </div>
              <div className="term-booking-drawer-body">
                <div className="term-booking-drawer-summary" data-testid="WEB-TERM-032">
                  <strong>{selectedTeacherLabel || 'No teacher assigned'}</strong>
                  <span>{slotNameDraft || slotForm.label || selectedSlot?.label || selectedSlot?.title || 'Weekly slot'}</span>
                  {(drawerSlotDay || drawerSlotTime) && <span>{drawerSlotDay} {drawerSlotTime}</span>}
                  {editingBooking && (
                    <span>{TERM_BOOKING_STATUS_LABELS[bookingForm.status] || bookingForm.status || 'Pre-filled'}</span>
                  )}
                  {bookingForm.source_booking_id && (
                    <span>Renewed from booking #{bookingForm.source_booking_id}</span>
                  )}
                </div>
                <div className="term-template-grid">
            <label>
              Slot name
              <input value={slotNameDraft} onChange={(event) => setSlotNameDraft(event.target.value)} placeholder="Adult salsa" />
            </label>
            <label>
              Teacher
              <select name="teacher_id" value={bookingForm.teacher_id} onChange={(event) => {
                const selectedTeacher = teacherOptions.find((teacher) => String(userIdentifier(teacher)) === String(event.target.value));
                setBookingForm({
                  ...bookingForm,
                  teacher_id: event.target.value,
                  teacher_email: selectedTeacher?.primary_email || selectedTeacher?.email || '',
                  teacher_name: selectedTeacher ? userDisplayName(selectedTeacher) : '',
                });
              }}>
                <option value="">Select teacher</option>
                {teacherOptions.map((teacher) => (
                  <option key={userIdentifier(teacher)} value={userIdentifier(teacher)}>
                    {userDisplayName(teacher)}
                  </option>
                ))}
              </select>
              {teacherOptions.length === 0 && (
                <span className="term-field-hint">No teacher users found in this organisation.</span>
              )}
              {!bookingForm.teacher_id && (
                <span className="term-field-hint" data-testid="WEB-TERM-032-no-teacher">No teacher assigned</span>
              )}
            </label>
            <label>
              Unavailable weeks
              <input name="unavailable_weeks" value={bookingForm.unavailable_weeks} onChange={(event) => setBookingForm({ ...bookingForm, unavailable_weeks: event.target.value })} placeholder="8, 12" />
            </label>
            <label data-testid="WEB-TERM-010">
              Unavailable dates
              <input name="unavailable_dates" value={bookingForm.unavailable_dates} onChange={(event) => setBookingForm({ ...bookingForm, unavailable_dates: event.target.value })} placeholder="2026-10-31, 2026-12-24" />
            </label>
            <label data-testid="WEB-TERM-009">
              Source booking ID
              <input name="source_booking_id" value={bookingForm.source_booking_id} onChange={(event) => setBookingForm({ ...bookingForm, source_booking_id: event.target.value })} placeholder="Previous booking ID" />
            </label>
            <label>
              Billable start
              <input name="billable_start_date" type="date" value={bookingForm.billable_start_date} onChange={(event) => setBookingForm({ ...bookingForm, billable_start_date: event.target.value })} />
            </label>
            <label>
              Billable end
              <input name="billable_end_date" type="date" value={bookingForm.billable_end_date} onChange={(event) => setBookingForm({ ...bookingForm, billable_end_date: event.target.value })} />
            </label>
            <label data-testid="WEB-TERM-012">
              Booking status
              <select name="status" value={bookingForm.status} onChange={(event) => setBookingForm({ ...bookingForm, status: event.target.value })}>
                <option value="prefilled">Pre-filled</option>
                <option value="invited">Invited</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="admin_confirmed">Admin confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
            <label>
              Website programme
              <select name="class_programme_id" data-testid="term-class-programme-select" value={bookingForm.class_programme_id} onChange={(event) => setBookingForm({ ...bookingForm, class_programme_id: event.target.value })}>
                <option value="">Not linked</option>
                {programmes.filter((programme) => programme.status !== 'archived').map((programme) => (
                  <option key={programme.id} value={programme.id}>{programme.public_title || programme.title || programme.slug}</option>
                ))}
              </select>
            </label>
            <label className="term-checkbox-row compact">
              <input name="public_visible" data-testid="term-public-visible" type="checkbox" checked={boolValue(bookingForm.public_visible)} onChange={(event) => setBookingForm({ ...bookingForm, public_visible: event.target.checked })} />
              Show this term run on the public website
            </label>
            <label className="term-span-2">
              Public title override
              <input name="public_title_override" data-testid="term-public-title-override" value={bookingForm.public_title_override} onChange={(event) => setBookingForm({ ...bookingForm, public_title_override: event.target.value })} placeholder="Use the programme title" />
            </label>
            <label className="term-span-2">
              Public description override
              <textarea name="public_description_override" data-testid="term-public-description-override" rows="3" value={bookingForm.public_description_override} onChange={(event) => setBookingForm({ ...bookingForm, public_description_override: event.target.value })} placeholder="Use the programme description" />
            </label>
            <label className="term-checkbox-row compact">
              <input name="auto_renew_admin" type="checkbox" checked={boolValue(bookingForm.auto_renew_admin)} onChange={(event) => setBookingForm({ ...bookingForm, auto_renew_admin: event.target.checked })} />
              Admin auto-renew
            </label>
            <label className="term-checkbox-row compact" data-testid="WEB-TERM-008">
              <input name="auto_renew_user" type="checkbox" checked={boolValue(bookingForm.auto_renew_user)} onChange={(event) => setBookingForm({ ...bookingForm, auto_renew_user: event.target.checked })} disabled={!boolValue(bookingForm.auto_renew_admin)} />
              Teacher auto-renew
            </label>
            <label className="term-checkbox-row compact">
              <input name="auto_accept_admin" type="checkbox" checked={boolValue(bookingForm.auto_accept_admin)} onChange={(event) => setBookingForm({ ...bookingForm, auto_accept_admin: event.target.checked })} />
              Admin auto-accept
            </label>
            <label className="term-checkbox-row compact" data-testid="WEB-TERM-016">
              <input name="auto_accept_user" type="checkbox" checked={boolValue(bookingForm.auto_accept_user)} onChange={(event) => setBookingForm({ ...bookingForm, auto_accept_user: event.target.checked })} disabled={!boolValue(bookingForm.auto_accept_admin)} />
              Teacher auto-accept
            </label>
            {!editingBooking && (
              <label className="term-checkbox-row compact">
                <input name="send_invitation" type="checkbox" checked={boolValue(bookingForm.send_invitation)} onChange={(event) => setBookingForm({ ...bookingForm, send_invitation: event.target.checked })} />
                Send invitation now
              </label>
            )}
            <label className="term-span-2">
              Admin reason
              <input name="admin_reason" value={bookingForm.admin_reason} onChange={(event) => setBookingForm({ ...bookingForm, admin_reason: event.target.value })} placeholder="Required for post-publish booking changes" />
            </label>
            <label className="term-span-2">
              Notes
              <textarea name="notes" rows="3" value={bookingForm.notes} onChange={(event) => setBookingForm({ ...bookingForm, notes: event.target.value })} />
            </label>
                </div>
              </div>
              <div className="drawer-actions term-action-row">
                <button type="submit" className="btn btn-primary" disabled={!bookingForm.teacher_id && !bookingForm.source_booking_id}>{editingBooking ? 'Save Booking' : 'Add Booking'}</button>
                <button type="button" className="btn btn-secondary" onClick={closeBookingDialog}>Cancel</button>
                {selectedTermId && (
                  <button type="button" className="btn btn-secondary" onClick={() => termApi.sendTermInvitations(selectedTermId).then(() => setStatus('Pending term invitations sent.')).catch((err) => setError(err.response?.data?.message || err.response?.data?.code || 'Unable to send invitations.'))}>
                    Send All Pending Invitations
                  </button>
                )}
              </div>
            </form>
          </aside>
        </div>
      )}
    </section>
  );
};

export default TermManagementPanel;
